<script setup>
import BaseMetricStrip from '@/components/base/BaseMetricStrip.vue'
import TransportLoadedItemsTable from './TransportLoadedItemsTable.vue'
import BaseInfoLine from '../base/BaseInfoLine.vue'

import { computed, defineEmits, defineProps } from 'vue'
import {
  formatCurrency,
  formatDate,
  formatDecimals
} from '@/helpers/stringHelpers'
import { isInvoicedStatus } from '@/constants/invoiceStatus.js'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  isCartonItem: {
    type: Boolean,
    default: false
  },
  cartonHeaders: {
    type: Array,
    default: () => []
  },
  porkerHeaders: {
    type: Array,
    default: () => []
  },
  freightCode: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['update-freight', 'update-invoice'])

const loadedItemHeaders = computed(() => {
  return props.isCartonItem ? props.cartonHeaders : props.porkerHeaders
})

const metrics = computed(() => {
  const {
    loaded_date,
    total_weight,
    required_date,
    total_cartons,
    total_pigs
  } = props.item?.job_description || {}
  return [
    { label: 'Date Loaded', value: loaded_date },
    { label: 'Total Weight', value: `${formatDecimals(total_weight)}kg` },
    { label: 'Date Required', value: required_date, show: props.isCartonItem },
    { label: 'Total Cartons', value: total_cartons, show: props.isCartonItem },
    { label: 'Total Pigs', value: total_pigs, show: !props.isCartonItem }
  ]
})

const isMultipleFreight = computed(() => {
  const { transport_cost } = props.item?.job_description || {}

  if (!transport_cost) return false
  if (transport_cost.length === 1) return false

  return true
})

const isLocked = computed(() => {
  return isInvoicedStatus(props.item.invoice_status)
})

const invoiceTooltipTitle = computed(() => {
  return isLocked.value ? 'Invoice Locked' : 'Update Invoice Number'
})

const invoiceTooltipText = computed(() => {
  return isLocked.value ? 'This docket has been invoiced.' : 'Click to edit.'
})

const freightTooltipTitle = computed(() => {
  return isLocked.value ? 'Delivery Company Locked' : 'Update Delivery Company'
})

const freightTooltipText = computed(() => {
  return isLocked.value
    ? 'This docket has been invoiced.'
    : 'Click on names to edit.'
})

const updateInvoice = () => {
  if (isLocked.value) return

  emit('update-invoice', props.item)
}

const updateFreight = () => {
  if (isLocked.value) return

  emit('update-freight', props.item)
}
</script>

<template>
  <v-card class="mb-3">
    <v-card-text>
      <v-row no-gutters class="mb-4 align-center">
        <div class="font-weight-bold grey--text text--darken-1 font-italic">
          {{ formatDate(item.date) }}
        </div>

        <v-spacer />

        <div class="d-flex align-baseline text-left">
          <BaseInfoLine
            label="Invoice"
            :value="item.invoice"
            editable
            :tooltip-title="invoiceTooltipTitle"
            :tooltip-text="invoiceTooltipText"
            :tooltip-icon="isLocked ? ['fas', 'lock'] : ['fas', 'edit']"
            @click="updateInvoice"
          />

          <div class="text-no-wrap ml-3">
            <span class="font-weight-bold grey--text text--darken-1 mr-1">
              Docket:
            </span>
            <span class="font-italic">{{ item.docket }}</span>
          </div>
        </div>
      </v-row>

      <v-row no-gutters class="mb-4">
        <h4 class="mb-0">{{ item.destination.name }}</h4>
      </v-row>

      <div class="mb-2 d-flex justify-start">
        <BaseInfoLine
          label="Delivered By"
          :value="freightCode(item.delivery_co_ids)"
          icon="fas fa-truck-arrow-right"
          icon-class="text-info"
          editable
          :tooltip-title="freightTooltipTitle"
          :tooltip-text="freightTooltipText"
          :tooltip-icon="isLocked ? ['fas', 'lock'] : ['fas', 'edit']"
          @click="updateFreight"
        />
      </div>

      <v-row v-if="isMultipleFreight" no-gutters class="mb-2 align-start">
        <small class="d-flex ml-2 align-center">
          <font-awesome-icon
            icon="fas fa-dollar-sign"
            class="mr-1 text-warning"
          />
          <span class="mr-2 grey--text text--darken-1 multiple-freight-label"
            >Cost by Freight:</span
          >
        </small>

        <v-col class="d-flex flex-column align-start">
          <p
            v-for="cost in item.job_description.transport_cost"
            :key="cost.id"
            class="mr-3 mb-0"
          >
            <small>
              {{ freightCode(cost.freight_company_id) }} -
              {{ formatCurrency(cost.cost_ex_gst) }}
            </small>
          </p>
        </v-col>
      </v-row>

      <div class="mb-2 d-flex justify-start">
        <BaseInfoLine
          label="Depot"
          :value="item.depot.name"
          icon="fas fa-map-marker-alt"
          icon-class="text-danger"
        />
      </div>

      <BaseMetricStrip
        :metrics="metrics"
        :total-price="`${formatCurrency(item.job_description.total_ex_GST)}`"
      />

      <div class="mt-2 text-left">
        <TransportLoadedItemsTable
          :items="item.loaded_items"
          :headers="loadedItemHeaders"
          :loading="loading"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.editable-display:hover {
  font-weight: 700;
  text-decoration: underline;
}

p {
  margin-bottom: 0;
  line-height: 1.1;
}

.multiple-freight-label {
  font-size: 14px;
  line-height: 1.1;
}
</style>
